// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The HUD's mute-all (07 §7).
//
// **On the machine and not only in Settings, because of when it is wanted.** The moment somebody
// reaches for this is the moment a person walks into the room, or a meeting starts, or a train
// carriage goes quiet — and a control you have to leave the game to reach is one nobody uses
// twice. That is the same argument focus mode won on (M3-45), and it is stronger here: leaving the
// game to find the setting means the machine is loud for the whole journey there.
//
// **One button, not two.** Music and effects have their own controls, and they live on the
// settings page, because "which parts of this do I want" is a decision made once and "make it stop
// now" is a decision made in a hurry. Putting both on the HUD would have meant six 44 px buttons
// across the top of a phone playfield that already needs focus mode to be big enough to play.
//
// **The label states the condition, never the action** — the rule M8-09 wrote for the voice
// controls, for the reason given there: "Mute" on a control that is already muted is how a mute
// button comes to lie. The accessible name is "Sound on" or "Sound off"; `aria-pressed` says it a
// second way; the `title` is the one place an action belongs and says what a press would do.
//
// **A speaker cabinet, not the cone `VoiceControls` draws.** In the VR arcade both controls are on
// screen at once and they mute completely different things — this one the machine, that one the
// other players — so two speakers side by side would be two buttons a player has to press to tell
// apart. A grille with a woofer and a tweeter is what the sound in question actually comes out of.
//
// Geometry rather than an emoji, like every other glyph here: an emoji is a picture from the
// operating system's font, so it is a different picture on Windows, Android and the Quest browser,
// and a box wherever the font is monochrome.

import { useLocalisation } from '../i18n/useLanguage'

export interface SoundControlProps {
  /** The machine's own sound is off. */
  readonly muted: boolean
  readonly onToggle: () => void
}

export function SoundControl({ muted, onToggle }: SoundControlProps): React.JSX.Element {
  const { t } = useLocalisation()
  return (
    <button
      type="button"
      className={muted ? 'button button--quiet stage__mute stage__mute--off' : 'button button--quiet stage__mute'}
      title={muted ? t('stage.sound-on-title') : t('stage.sound-off-title')}
      aria-pressed={muted}
      onClick={onToggle}
    >
      <CabinetGlyph muted={muted} />
      <span className="sr-only">{muted ? t('stage.sound-off') : t('stage.sound-on')}</span>
    </button>
  )
}

/**
 * A speaker cabinet, struck through when the sound is off.
 *
 * `aria-hidden`, always: the button takes its name from the `sr-only` span beside it, and a screen
 * reader announcing the drawing as well would read the control twice.
 */
function CabinetGlyph({ muted }: { readonly muted: boolean }): React.JSX.Element {
  return (
    <svg
      width={20}
      height={20}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M4 9v6h4l5 4V5L8 9H4z" />
      {muted ? <path d="M17 8l5 8M22 8l-5 8" /> : <path d="M16.5 8.5a5 5 0 0 1 0 7" />}
    </svg>
  )
}
